/**
 * URL mapping utilities for OS layout deep linking.
 *
 * Provides functions to:
 * - Build URLs from window state (appId + params)
 * - Parse URLs into window configurations for opening windows
 */

import { MODULE_REGISTRY } from "@/config/modules";
import { formatScenarioMetric } from "@/constants/scenarios";
import { getInsightPackWindowTitle } from "@/components/pulse/insightPacks";
import { NAV_KEYS } from "@/navigation/keys";
import { missionControlCodec } from "@/navigation/apps/missionControlLocation";
import { buildDeepLinkUrl, parseDeepLinkUrl } from "@/navigation/deepLinkRegistry";
import { homeCodec } from "@/navigation/apps/homeLocation";
import {
  buildCalculatorsPath,
  calculatorsWindowTitle,
  parseCalculatorsFromSearchParams,
  serializeCalculatorsToOsParams,
} from "@/navigation/apps/calculatorsLocation";
import {
  parseCaseCheckingFromRoute,
  serializeCaseCheckingToOsParams,
} from "@/navigation/apps/caseCheckingLocation";
import {
  parseDisclosureFromRoute,
  serializeDisclosureToOsParams,
} from "@/navigation/apps/disclosureLocation";

/**
 * Configuration for opening a window, matching OpenWindowConfig in OsContext.
 */
export interface WindowUrlConfig {
  appId: string;
  title: string;
  params: Record<string, string>;
}

/**
 * Strip origin from absolute URLs so ``parseUrlToWindowConfig`` receives a path.
 * Share helpers (e.g. Guardians) emit full URLs; OS navigation expects ``/path?query``.
 */
export function normalizeOsNavigateTarget(to: string): string {
  if (/^https?:\/\//i.test(to)) {
    try {
      const url = new URL(to);
      return `${url.pathname}${url.search}`;
    } catch {
      return to;
    }
  }
  return to;
}

/**
 * Mapping from route path segments to appId.
 * This centralizes the mapping that was previously duplicated in GlobalSearchDialog.
 */
const ROUTE_TO_APP_ID: Record<string, string> = {
  // Core apps
  dashboard: "dashboard",
  screener: "screener",
  playground: "playground",
  documents: "documents",
  ops: "ops",
  settings: "settings",
  reports: "reports",
  meetings: "meetings",
  meeting: "meeting",
  inbox: "inbox",

  // Utility apps (not in MODULE_REGISTRY)
  minesweeper: "minesweeper",
  "fca-portal": "fca-portal",
  "mission-kit": "mission-kit",
  codex: "codex",

  // Worker/Pod apps (special handling for directory vs single)
  worker: "worker",
  pod: "pods",

  // Portfolio/Product apps
  portfolio: "portfolio",
  product: "product",

  // Specialized apps
  "report-builder": "report-builder",
  "scenario-tracker": "scenario-tracker",
  "what-if": "what-if",
  "cash-flow": "cash-flow",
  "add-products": "add-products",
  "trade-blotter": "trade-blotter",
  "portfolio-builder": "portfolio-builder",
  "data-hub": "data-hub",
  "benchmark-builder": "benchmark-builder",
  "monte-carlo": "monte-carlo",
  cmas: "cmas",
  optimiser: "optimiser",
  "factor-analysis": "factor-analysis",
  "portfolio-lab": "portfolio-lab",
  "advisor-dashboard": "advisor-dashboard",
  rebalancing: "rebalancing",
  twr: "twr",
  irr: "irr",
  calculators: "calculators",
  "case-checking": "case-checking",
  disclosures: "disclosures",
  "pulse-v2": "pulse-v2",
  leads: "leads",
};

/**
 * Mapping for ops sub-routes to their own appIds.
 * These are separate apps that can each open in their own window.
 */
const OPS_SUB_ROUTES: Record<string, string> = {
  checklists: "ops-checklists",
  checker: "ops-checker",
  "system-jobs": "ops-system-jobs",
  "task-manager": "ops-task-manager",
  "mission-control-ops": "ops-mission-control-ops",
  "etl-runs": "ops-etl-runs",
  "db-versions": "ops-db-versions",
  "worker-templates": "ops-worker-templates",
  "etl-audit": "ops-etl-audit",
  snapshots: "ops-snapshots",
  "access-permissions": "ops-access-permissions",
  refinitiv: "ops-refinitiv",
  msci: "ops-msci",
  seccl: "ops-seccl",
  guardians: "ops-guardians",
  "cache-management": "ops-cache-management",
  "data-sentinel": "ops-data-sentinel",
  "data-gathering": "ops-data-gathering",
  "agent-review": "ops-agent-review",
  "ace-task-review": "ops-ace-task-review",
  "feature-flags": "ops-feature-flags",
  "external-data": "ops-external-data",
  "news-ingest": "ops-news-ingest",
  "automatic-ingestions": "ops-automatic-ingestions",
  "magic-upload": "ops-magic-upload",
  progeny: "ops-progeny",
  curo: "ops-curo",
  "meeting-connectors": "ops-meeting-connectors",
  "temporal-rag": "ops-temporal-rag",
  "memory-codex": "ops-memory-codex",
  "case-checking": "ops-case-checking",
};

const SCENARIO_TRACKER_TAB_APPS: Record<
  string,
  { tab: string; title: string }
> = {
  "scenario-tracker": {
    tab: "exec-summary",
    title: "Scenario Key Insights",
  },
  "scenario-tracker-newsflow": {
    tab: "news-flow",
    title: "Scenario Newsflow",
  },
  "scenario-tracker-directionality": {
    tab: "master-analysis",
    title: "Scenario Directionality",
  },
  "scenario-tracker-heatmap": {
    tab: "summary",
    title: "Scenario Heatmap",
  },
  "scenario-tracker-deep-dive": {
    tab: "deep-dive",
    title: "Scenario Deep Dive",
  },
  "scenario-tracker-metric-projections": {
    tab: "metric-projections",
    title: "Scenario Metric Projections",
  },
};

const SCENARIO_TRACKER_APP_BY_TAB = Object.fromEntries(
  Object.entries(SCENARIO_TRACKER_TAB_APPS).map(([appId, config]) => [
    config.tab,
    appId,
  ])
) as Record<string, string>;

/**
 * Mapping from appId to route path.
 * Generated from MODULE_REGISTRY for consistency.
 */
function getAppIdToRoutePath(): Record<string, string> {
  const map: Record<string, string> = {};

  Object.values(MODULE_REGISTRY).forEach((module) => {
    const appId = module.osAppId || module.routePath.replace(/^\//, "");
    map[appId] = module.routePath;
  });

  // Add special cases not in MODULE_REGISTRY
  map["inbox"] = "/dashboard"; // Inbox is part of dashboard
  map["pod-instance"] = "/pod"; // Pod instance uses /pod/:template/:id
  map["worker"] = "/worker"; // Single worker view
  map["workers"] = "/worker/directory"; // Worker directory
  map["worker-create"] = "/worker/create"; // Worker creation
  map["minesweeper"] = "/minesweeper"; // Utility game app
  map["fca-portal"] = "/fca-portal"; // FCA demo applicant portal
  map["mission-kit"] = "/mission-kit";
  map["codex"] = "/codex"; // Workforce registry showcase

  return map;
}

const APP_ID_TO_ROUTE_PATH = getAppIdToRoutePath();

/**
 * Build a shareable URL from window appId and params.
 *
 * @param appId - The app ID (e.g., "worker", "portfolio", "pods")
 * @param params - Window params (e.g., { id: "123" })
 * @returns Relative URL path and query (`/path?query`)
 *
 * @example
 * buildWindowUrl("worker", { id: "abc123" })
 * // => "https://app.example.com/worker/abc123"
 *
 * buildWindowUrl("pod-instance", { templateId: "tmpl", instanceId: "inst" })
 * // => "https://app.example.com/pod/tmpl/inst"
 */
export function buildWindowUrl(
  appId: string,
  params: Record<string, string>
): string {

  const registryUrl = buildDeepLinkUrl(appId, params);
  if (registryUrl) {
    return registryUrl;
  }

  // Handle special cases
  switch (appId) {
    case "pod-instance":
      // /pod/:template/:id
      if (params.templateId && params.instanceId) {
        return `/pod/${params.templateId}/${params.instanceId}`;
      }
      // Pod instance without full params - link to pod directory
      if (params.templateId) {
        return `/pod/${params.templateId}`;
      }
      return `/pod/directory`;

    case "missions": {
      const focus = String(params.focus ?? "");
      if (focus.startsWith("mission:")) {
        return missionControlCodec.toUrl(
          missionControlCodec.fromParams({
            view: "missions",
            focus,
            visibility: String(params.visibility ?? "all"),
          }),
        );
      }
      const missionsParams = new URLSearchParams();
      if (params.focus) missionsParams.set("focus", params.focus);
      const missionsSearch = missionsParams.toString();
      return missionsSearch
        ? `/missions?${missionsSearch}`
        : `/missions`;
    }

    case "task-review": {
      if (!params.taskId) {
        return `/home`;
      }
      const reviewParams = new URLSearchParams();
      if (params.focus === "letter") {
        reviewParams.set("focus", "letter");
      }
      if (params.mode === "progress") {
        reviewParams.set("mode", "progress");
      }
      const reviewSearch = reviewParams.toString();
      const reviewPath = `/review/${params.taskId}`;
      return reviewSearch ? `${reviewPath}?${reviewSearch}` : reviewPath;
    }

    case "dg-monitoring-task":
      if (params.taskId) {
        return `/ops/data-gathering/monitoring/${encodeURIComponent(params.taskId)}`;
      }
      return `/ops/data-gathering`;

    case "ops-dg-case": {
      if (!params.faClientId) {
        return `/ops/data-gathering`;
      }
      const caseParams = new URLSearchParams();
      if (params.displayName) caseParams.set("name", String(params.displayName));
      const caseSearch = caseParams.toString();
      const casePath = `/ops/data-gathering/case/${encodeURIComponent(String(params.faClientId))}`;
      return caseSearch ? `${casePath}?${caseSearch}` : casePath;
    }

    case "ops-data-gathering":
      return `/ops/data-gathering`;

    case "artifacts":
      return `/artifacts`;

    case "pods":
      // /pod/:id for template, or /pod/directory for listing
      if (params.id) {
        const podParams = new URLSearchParams();
        if (params.tab) {
          podParams.set("tab", params.tab);
        }
        const podSearch = podParams.toString();
        return podSearch
          ? `/pod/${params.id}?${podSearch}`
          : `/pod/${params.id}`;
      }
      return `/pod/directory`;

    case "workers":
      // /worker/directory for listing
      if (params.layout === "directory") {
        return `/worker/directory`;
      }
      return `/worker/directory`;

    case "worker":
      // /worker/:id
      if (params.id) {
        const workerParams = new URLSearchParams();
        const conversation = params.conversation ?? params.conversationId;
        if (conversation) {
          workerParams.set(NAV_KEYS.conversation, conversation);
        }
        const workerSearch = workerParams.toString();
        return workerSearch
          ? `/worker/${params.id}?${workerSearch}`
          : `/worker/${params.id}`;
      }
      // Worker without id - link to worker directory
      return `/worker/directory`;

    case "portfolio":
      // /portfolio/:id
      if (params.id) {
        return `/portfolio/${params.id}`;
      }
      // Portfolio without id - link to screener
      return `/screener`;

    case "product":
      // /product/:id
      if (params.id) {
        return `/product/${params.id}`;
      }
      // Product without id - link to screener
      return `/screener`;

    case "report": {
      const reportParams = new URLSearchParams();
      if (params.config) {
        reportParams.set("config", params.config);
      }
      if (params.template) {
        reportParams.set("template", params.template);
      }
      if (params.presentation) {
        reportParams.set("presentation", params.presentation);
      }
      if (params.landscape) {
        reportParams.set("landscape", params.landscape);
      }
      const reportSearch = reportParams.toString();
      return reportSearch
        ? `/report?${reportSearch}`
        : `/report`;
    }

    case "pdf-viewer": {
      const pdfParams = new URLSearchParams();
      if (params.config) {
        pdfParams.set("config", params.config);
      }
      if (params.template) {
        pdfParams.set("template", params.template);
      }
      if (params.landscape) {
        pdfParams.set("landscape", params.landscape);
      }
      const pdfSearch = pdfParams.toString();
      return pdfSearch ? `/pdf?${pdfSearch}` : `/pdf`;
    }

    case "report-builder": {
      // /report-builder?report_id=... or /report-builder for new
      const rbParams = new URLSearchParams();
      if (params.report_id) {
        rbParams.set("report_id", params.report_id);
      }
      if (params.new === "true") {
        rbParams.set("new", "true");
      }
      if (params.mode === "pro") {
        rbParams.set("mode", "pro");
      }
      const rbSearch = rbParams.toString();
      return rbSearch
        ? `/report-builder?${rbSearch}`
        : `/report-builder`;
    }

    case "pulse-v2": {
      const pulseParams = new URLSearchParams();
      if (params.packId) pulseParams.set("pack", params.packId);
      const pulseSearch = pulseParams.toString();
      return pulseSearch
        ? `/pulse-v2?${pulseSearch}`
        : `/pulse-v2`;
    }

    case "add-products": {
      const addParams = new URLSearchParams();
      if (params.mode) addParams.set("mode", params.mode);
      if (params.assetClass) addParams.set("assetClass", params.assetClass);
      const addSearch = addParams.toString();
      return addSearch ? `/add-products?${addSearch}` : `/add-products`;
    }

    case "what-if": {
      // /what-if or /what-if/portfolio/:portfolioId or /what-if/portfolio/:portfolioId/scenario/:scenarioId
      if (params.portfolioId) {
        if (params.scenarioId) {
          return `/what-if/portfolio/${params.portfolioId}/scenario/${params.scenarioId}`;
        }
        return `/what-if/portfolio/${params.portfolioId}`;
      }
      return `/what-if`;
    }

    case "scenario-tracker": {
      if (params.id) {
        return `/scenario-tracker/${encodeURIComponent(params.id)}`;
      }
      const tab = SCENARIO_TRACKER_TAB_APPS[appId]?.tab;
      return tab && tab !== "exec-summary"
        ? `/scenario-tracker?tab=${encodeURIComponent(tab)}`
        : `/scenario-tracker`;
    }

    case "scenario-tracker-newsflow":
    case "scenario-tracker-directionality":
    case "scenario-tracker-heatmap":
    case "scenario-tracker-deep-dive":
    case "scenario-tracker-metric-projections": {
      const tab = SCENARIO_TRACKER_TAB_APPS[appId]?.tab;
      return tab && tab !== "exec-summary"
        ? `/scenario-tracker?tab=${encodeURIComponent(tab)}`
        : `/scenario-tracker`;
    }

    case "scenario-metric-detail": {
      const detailParams = new URLSearchParams();
      [
        "instanceId",
        "analysisId",
        "category",
        "metric",
        "scenario",
        "scenarioCommentary",
      ].forEach((key) => {
        if (params[key]) detailParams.set(key, params[key]);
      });
      return `/scenario-tracker/detail?${detailParams.toString()}`;
    }

    case "scenario-article-detail": {
      const articleParams = new URLSearchParams();
      [
        "instanceId",
        "articleId",
        "headline",
        "source",
        "publishedDate",
        "url",
      ].forEach((key) => {
        if (params[key]) articleParams.set(key, params[key]);
      });
      return `/scenario-tracker/article?${articleParams.toString()}`;
    }

    case "document":
      if (params.id) {
        return `/documents/${encodeURIComponent(params.id)}`;
      }
      return `/documents`;

    case "portfolios":
      // OS portfolio/product peek window (screener click or workers)
      if (params.entityId) {
        if (params.entityType === "product") {
          return `/product/${encodeURIComponent(params.entityId)}`;
        }
        return `/portfolio/${encodeURIComponent(params.entityId)}`;
      }
      return `/screener`;

    case "worker-create":
      // Worker creation - link to worker creation page
      if (params.workerId) {
        return `/worker/create?workerId=${encodeURIComponent(params.workerId)}`;
      }
      return `/worker/create`;

    case "settings": {
      const settingsParams = new URLSearchParams();
      if (params.section) {
        settingsParams.set("section", params.section);
      }
      const settingsSearch = settingsParams.toString();
      return settingsSearch
        ? `/settings?${settingsSearch}`
        : `/settings`;
    }

    case "home":
      return homeCodec.toUrl(homeCodec.fromParams(params));

    case "inbox":
      return `/dashboard`;

    case "minesweeper":
      // Minesweeper game
      return `/minesweeper`;

    case "mission-kit-blueprint": {
      const view = params.view;
      const query = view ? `?view=${encodeURIComponent(view)}` : "";
      return `/mission-kit/blueprints/${encodeURIComponent(params.missionId)}${query}`;
    }

    case "mission-kit": {
      if (params.kitSlug) {
        return `/mission-kit/kits/${encodeURIComponent(params.kitSlug)}`;
      }
      const tab = params.tab;
      const query = tab ? `?tab=${encodeURIComponent(tab)}` : "";
      return `/mission-kit${query}`;
    }

    case "codex":
      if (params.taskKey && !params.missionId && !params.agentId) {
        return `/codex/tasks/${encodeURIComponent(params.taskKey)}`;
      }
      if (params.missionId && params.taskKey) {
        return `/codex/missions/${encodeURIComponent(params.missionId)}/tasks/${encodeURIComponent(params.taskKey)}`;
      }
      if (params.agentId) {
        return `/codex/agents/${encodeURIComponent(params.agentId)}`;
      }
      if (params.missionId) {
        const view = params.view;
        const query = view ? `?view=${encodeURIComponent(view)}` : "";
        return `/codex/missions/${encodeURIComponent(params.missionId)}${query}`;
      }
      if (params.library && params.library !== "home") {
        return `/codex?library=${encodeURIComponent(params.library)}`;
      }
      return `/codex`;

    case "company-brain":
      return `/codex/company-brain`;

    case "ops-checklists": {
      if (params.checklistView === "review" && params.runId) {
        return `/ops/checklists/review/${encodeURIComponent(params.runId)}`;
      }
      if (params.runId) {
        return `/ops/checklists/run/${encodeURIComponent(params.runId)}`;
      }
      if (params.checklistView === "history") {
        return `/ops/checklists/history`;
      }
      if (params.checklistView === "config") {
        return `/ops/checklists/config`;
      }
      if (params.checklistView === "issues") {
        return `/ops/checklists/issues`;
      }
      return `/ops/checklists`;
    }

    case "ops-guardians-conversation": {
      const cParams = new URLSearchParams();
      if (params.mission) cParams.set("mission", params.mission);
      if (params.task) cParams.set("task", params.task);
      if (params.focus) cParams.set("focus", params.focus);
      const cSearch = cParams.toString();
      return cSearch
        ? `/ops/guardians/conversation?${cSearch}`
        : `/ops/guardians/conversation`;
    }

    case "ops-curo":
      return `/ops/curo`;

    case "ops-meeting-connectors":
      return `/ops/meeting-connectors`;

    case "ops-progeny": {
      if (params.subRoute === "webhooks") {
        return `/ops/progeny/webhooks`;
      }
      if (params.subRoute === "processing") {
        return `/ops/progeny/processing`;
      }
      if (params.subRoute === "sam-testing") {
        return `/ops/progeny/sam-testing`;
      }
      if (params.subRoute === "review" && params.reviewId) {
        return `/ops/progeny/review/${encodeURIComponent(params.reviewId)}`;
      }
      return `/ops/progeny`;
    }

    case "ops-progeny-file-checking":
      return `/ops/progeny/file-checking`;

    case "ops-case-checking": {
      if (params.view === "comparison") {
        return `/ops/case-checking?view=comparison`;
      }
      if (params.view === "costs") {
        return `/ops/case-checking?view=costs`;
      }
      return `/ops/case-checking`;
    }

    case "ops-progeny-advisor":
      return `/ops/progeny/advisor`;

    case "ops-agent-testing": {
      if (params.abTestId) {
        return `/ops/agent-testing/ab-tests/${encodeURIComponent(params.abTestId)}`;
      }
      if (params.view === "configure") {
        return `/ops/agent-testing/configure`;
      }
      return `/ops/agent-testing`;
    }

    case "ops-temporal-rag":
      return `/ops/temporal-rag`;

    case "ops-memory-codex":
      return `/ops/memory-codex`;

    case "ops-refinitiv":
      return `/ops/refinitiv`;

    case "ops-msci":
      return `/ops/msci`;

    case "ops-seccl":
      return `/ops/seccl`;

    case "ops-agent-review":
      if (params.runId) {
        return `/ops/agent-review/case/${encodeURIComponent(params.runId)}`;
      }
      return `/ops/agent-review`;

    case "ops-ace-task-review":
      if (params.auditId) {
        return `/ops/ace-task-review/audit/${encodeURIComponent(params.auditId)}`;
      }
      return `/ops/ace-task-review`;

    case "ops-automatic-ingestions":
      if (params.pipeline === "daily-ihf") {
        return `/ops/automatic-ingestions/daily-ihf`;
      }
      if (params.pipeline === "daily-ihf-cash-fund-rec") {
        return `/ops/automatic-ingestions/daily-ihf-cash-fund-rec`;
      }
      return `/ops/automatic-ingestions`;

    case "ops-daily-ihf-ingest":
      return `/ops/automatic-ingestions/daily-ihf`;

    case "calculators": {
      const loc = {
        calculatorId: params.calculatorId,
        faClientId: params.faClientId,
      };
      return buildCalculatorsPath(loc);
    }

    default: {
      // Standard apps: use the route path directly
      const routePath = APP_ID_TO_ROUTE_PATH[appId];
      if (routePath) {
        // If params has an id, append it
        if (params.id) {
          return `${routePath}/${params.id}`;
        }
        return `${routePath}`;
      }
    }
  }

  // Fallback: if appId looks like a route path, use it directly
  // This handles apps like "reports", etc.
  if (appId && !appId.includes(" ")) {
    return `/${appId}`;
  }

  // Last resort fallback to dashboard
  return `/dashboard`;
}

/**
 * Parse a URL pathname into a window configuration.
 *
 * @param pathname - The URL pathname (e.g., "/worker/abc123")
 * @param search - Optional URL search string (e.g., "?id=123")
 * @returns WindowUrlConfig if parseable, null otherwise
 *
 * @example
 * parseUrlToWindowConfig("/worker/abc123")
 * // => { appId: "worker", title: "Worker", params: { id: "abc123" } }
 *
 * parseUrlToWindowConfig("/pod/tmpl/inst")
 * // => { appId: "pod-instance", title: "Pod Instance", params: { templateId: "tmpl", instanceId: "inst" } }
 */
export function parseUrlToWindowConfig(
  pathname: string,
  search?: string
): WindowUrlConfig | null {
  // Allow `pathname` to include `?query` (e.g. from useOsNavigate("/path?a=1"))
  const pathHasQuery = pathname.includes("?");
  const pathOnly = pathHasQuery ? pathname.slice(0, pathname.indexOf("?")) : pathname;
  const queryFromPath = pathHasQuery
    ? pathname.slice(pathname.indexOf("?") + 1)
    : "";
  const searchMerged = [queryFromPath, (search || "").replace(/^\?/, "")]
    .filter(Boolean)
    .join("&");

  const pathSegments = pathOnly.split("/").filter(Boolean);

  if (pathSegments.length === 0) {
    return null;
  }

  const registryConfig = parseDeepLinkUrl(pathOnly, searchMerged);
  if (registryConfig) {
    return registryConfig;
  }

  const basePath = pathSegments[0];
  const resourceId = pathSegments[1];
  const thirdSegment = pathSegments[2];

  // Parse query params
  const searchParams = new URLSearchParams(searchMerged);

  // Handle special cases first
  switch (basePath) {
    case "pod":
      // /pod/directory -> pods directory
      if (resourceId === "directory") {
        return {
          appId: "pods",
          title: "Pods",
          params: {},
        };
      }
      // /pod/create -> not a window, skip
      if (resourceId === "create") {
        return null;
      }
      // /pod/:template/:id -> pod instance
      if (resourceId && thirdSegment) {
        return {
          appId: "pod-instance",
          title: "Pod Instance",
          params: {
            templateId: resourceId,
            instanceId: thirdSegment,
          },
        };
      }
      // /pod/:id -> pod template
      if (resourceId) {
        const params: Record<string, string> = { id: resourceId };
        const tab = searchParams.get("tab");
        if (tab) params.tab = tab;
        return {
          appId: "pods",
          title: "Pod",
          params,
        };
      }
      return null;

    case "tasks": {
      if (
        resourceId &&
        thirdSegment !== "approval" &&
        thirdSegment !== "close-signoff" &&
        !thirdSegment
      ) {
        return {
          appId: "mission-control",
          title: "Mission Control",
          params: missionControlCodec.toParams(
            missionControlCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      return null;
    }

    case "mission-kit": {
      if (resourceId === "kits" && thirdSegment) {
        return {
          appId: "mission-kit",
          title: "Mission Kit",
          params: { kitSlug: decodeURIComponent(thirdSegment) },
        };
      }
      if (resourceId === "blueprints" && thirdSegment) {
        const params: Record<string, string> = {
          missionId: decodeURIComponent(thirdSegment),
        };
        const view = searchParams.get("view");
        if (
          view === "network" ||
          view === "brief" ||
          view === "blueprint" ||
          view === "requirements" ||
          view === "runbook" ||
          view === "automation"
        ) {
          params.view = view;
        }
        return {
          appId: "mission-kit-blueprint",
          title: "Mission Blueprint",
          params,
        };
      }
      const tab = searchParams.get("tab");
      const params: Record<string, string> = {};
      if (tab === "framework" || tab === "blueprints" || tab === "kits") {
        params.tab = tab;
      }
      return {
        appId: "mission-kit",
        title: "Mission Kit",
        params,
      };
    }

    case "review":
      if (resourceId) {
        const reviewParams: Record<string, string> = { taskId: resourceId };
        const reviewFocus = searchParams.get("focus");
        if (reviewFocus === "letter") {
          reviewParams.focus = "letter";
        }
        const reviewMode = searchParams.get("mode");
        if (reviewMode === "progress") {
          reviewParams.mode = "progress";
        }
        return {
          appId: "task-review",
          title: "Review",
          params: reviewParams,
        };
      }
      return null;

    case "worker":
      // /worker/directory -> workers directory
      if (resourceId === "directory") {
        return {
          appId: "workers",
          title: "Workers",
          params: { layout: "directory" },
        };
      }
      // /worker/create -> not a window, skip
      if (resourceId === "create") {
        const workerId = searchParams.get("workerId");
        const params: Record<string, string> = {};
        if (workerId) {
          params.workerId = workerId;
        }
        return {
          appId: "worker-create",
          title: "Create Worker",
          params,
        };
      }
      // /worker/:id -> single worker
      if (resourceId) {
        const params: Record<string, string> = { id: resourceId };
        const conversation =
          searchParams.get(NAV_KEYS.conversation) ?? searchParams.get(NAV_KEYS.conversationLegacy);
        if (conversation) {
          params.conversation = conversation;
        }
        return {
          appId: "worker",
          title: "Worker",
          params,
        };
      }
      // /worker -> workers directory (chat layout)
      return {
        appId: "workers",
        title: "Workers",
        params: {},
      };

    case "portfolio":
      if (resourceId) {
        return {
          appId: "portfolio",
          title: "Portfolio",
          params: { id: resourceId },
        };
      }
      return null;

    case "product":
      if (resourceId) {
        return {
          appId: "product",
          title: "Product",
          params: { id: resourceId },
        };
      }
      return null;

    case "ops":
      // Legacy /ops/curo/* sub-routes (old Progeny URLs) → Progeny
      if (
        resourceId === "curo" &&
        (thirdSegment === "webhooks" ||
          thirdSegment === "processing" ||
          (thirdSegment === "review" && pathSegments[3]))
      ) {
        const suffix = pathOnly.slice("/ops/curo".length);
        return parseUrlToWindowConfig(`/ops/progeny${suffix}`, search);
      }
      if (resourceId === "curo" && !thirdSegment) {
        return {
          appId: "ops-curo",
          title: "Curo Connect",
          params: {},
        };
      }
      // /ops -> ops hub
      if (!resourceId) {
        return {
          appId: "ops",
          title: "Operations",
          params: {},
        };
      }
      if (resourceId === "daily-ihf-ingest") {
        return {
          appId: "ops-automatic-ingestions",
          title: "Daily IHF Ingestion",
          params: { pipeline: "daily-ihf" },
        };
      }
      if (resourceId === "automatic-ingestions") {
        if (thirdSegment === "daily-ihf") {
          return {
            appId: "ops-automatic-ingestions",
            title: "Daily IHF Ingestion",
            params: { pipeline: "daily-ihf" },
          };
        }
        if (thirdSegment === "daily-ihf-cash-fund-rec") {
          return {
            appId: "ops-automatic-ingestions",
            title: "Daily IHF Cash & Fund Reconciliation",
            params: { pipeline: "daily-ihf-cash-fund-rec" },
          };
        }
        return {
          appId: "ops-automatic-ingestions",
          title: "Automatic Ingestions",
          params: {},
        };
      }
      if (resourceId === "case-checking") {
        const view = searchParams.get("view");
        const params: Record<string, string> = {};
        if (view === "comparison" || view === "costs") {
          params.view = view;
        }
        return {
          appId: "ops-case-checking",
          title: "Case Checking Ops",
          params,
        };
      }
      // /ops/progeny/file-checking -> dedicated app
      if (resourceId === "progeny" && thirdSegment === "file-checking") {
        return {
          appId: "ops-progeny-file-checking",
          title: "Progeny File Checking",
          params: {},
        };
      }
      // /ops/progeny/advisor -> dedicated app
      if (resourceId === "progeny" && thirdSegment === "advisor") {
        return {
          appId: "ops-progeny-advisor",
          title: "Progeny Advisor Ops",
          params: {},
        };
      }
      // /ops/progeny/webhooks -> ops-progeny with subRoute param
      if (resourceId === "progeny" && thirdSegment === "webhooks") {
        return {
          appId: "ops-progeny",
          title: "Progeny Webhooks",
          params: { subRoute: "webhooks" },
        };
      }
      // /ops/progeny/processing -> ops-progeny with subRoute param
      if (resourceId === "progeny" && thirdSegment === "processing") {
        return {
          appId: "ops-progeny",
          title: "Progeny Processing",
          params: { subRoute: "processing" },
        };
      }
      if (resourceId === "progeny" && thirdSegment === "sam-testing") {
        return {
          appId: "ops-progeny",
          title: "Case reviews",
          params: { subRoute: "sam-testing" },
        };
      }
      // /ops/progeny/review/:reviewId (must precede generic OPS_SUB_ROUTES for progeny)
      if (
        resourceId === "progeny" &&
        thirdSegment === "review" &&
        pathSegments[3]
      ) {
        return {
          appId: "ops-progeny",
          title: "Progeny Review",
          params: {
            subRoute: "review",
            reviewId: pathSegments[3],
          },
        };
      }
      // /ops/checklists/... (must precede generic OPS_SUB_ROUTES for checklists)
      if (resourceId === "checklists") {
        const fourthSegment = pathSegments[3];
        if (thirdSegment === "run" && fourthSegment) {
          return {
            appId: "ops-checklists",
            title: "Checklist Run",
            params: { runId: fourthSegment },
          };
        }
        if (thirdSegment === "review" && fourthSegment) {
          return {
            appId: "ops-checklists",
            title: "Checklist Review",
            params: { runId: fourthSegment, checklistView: "review" },
          };
        }
        if (thirdSegment === "history") {
          return {
            appId: "ops-checklists",
            title: "Checklist History",
            params: { checklistView: "history" },
          };
        }
        if (thirdSegment === "config") {
          return {
            appId: "ops-checklists",
            title: "Checklist Config",
            params: { checklistView: "config" },
          };
        }
        if (thirdSegment === "issues") {
          return {
            appId: "ops-checklists",
            title: "Checklist Issues",
            params: { checklistView: "issues" },
          };
        }
        if (!thirdSegment) {
          return {
            appId: "ops-checklists",
            title: "Ops Checklists",
            params: {},
          };
        }
        return {
          appId: "ops-checklists",
          title: "Ops Checklists",
          params: {},
        };
      }
      // /ops/agent-testing/ab-tests/:id (before generic OPS_SUB_ROUTES)
      if (
        resourceId === "agent-testing" &&
        thirdSegment === "ab-tests" &&
        pathSegments[3]
      ) {
        return {
          appId: "ops-agent-testing",
          title: "Agent A/B Test Results",
          params: { abTestId: pathSegments[3] },
        };
      }
      if (resourceId === "agent-testing" && thirdSegment === "configure") {
        return {
          appId: "ops-agent-testing",
          title: "Configure Agent A/B Test",
          params: { view: "configure" },
        };
      }
      if (resourceId === "agent-testing") {
        return {
          appId: "ops-agent-testing",
          title: "Agent A/B Testing",
          params: {},
        };
      }
      // /ops/agent-review/case/:runId (must precede generic OPS_SUB_ROUTES for agent-review)
      if (resourceId === "agent-review") {
        if (thirdSegment === "case" && pathSegments[3]) {
          return {
            appId: "ops-agent-review",
            title: "Agent Review Case",
            params: { runId: pathSegments[3] },
          };
        }
        if (!thirdSegment) {
          return {
            appId: "ops-agent-review",
            title: "Agent Review",
            params: {},
          };
        }
      }
      // /ops/ace-task-review/audit/:auditId (must precede generic OPS_SUB_ROUTES)
      if (resourceId === "ace-task-review") {
        if (thirdSegment === "audit" && pathSegments[3]) {
          return {
            appId: "ops-ace-task-review",
            title: "Ace Task Review Audit",
            params: { auditId: pathSegments[3] },
          };
        }
        if (!thirdSegment) {
          return {
            appId: "ops-ace-task-review",
            title: "Ace Task Review",
            params: {},
          };
        }
      }
      // /ops/:subroute -> flat ops tools (msci, refinitiv, etl-runs, …)
      if (OPS_SUB_ROUTES[resourceId]) {
        return {
          appId: OPS_SUB_ROUTES[resourceId],
          title: formatAppTitle(OPS_SUB_ROUTES[resourceId]),
          params: {},
        };
      }
      // Fallback to ops hub for unknown sub-routes
      return {
        appId: "ops",
        title: "Operations",
        params: {},
      };

    case "report": {
      const config = searchParams.get("config");
      const template = searchParams.get("template");
      if (!config || !template) {
        return null;
      }
      const params: Record<string, string> = { config, template };
      const presentation = searchParams.get("presentation");
      if (presentation) {
        params.presentation = presentation;
      }
      const landscape = searchParams.get("landscape");
      if (landscape) {
        params.landscape = landscape;
      }
      return {
        appId: "report",
        title: "Report",
        params,
      };
    }

    case "pdf": {
      const config = searchParams.get("config");
      const template = searchParams.get("template");
      if (!config || !template) {
        return null;
      }
      const params: Record<string, string> = { config, template };
      const landscape = searchParams.get("landscape");
      if (landscape) {
        params.landscape = landscape;
      }
      return {
        appId: "pdf-viewer",
        title: "Report PDF",
        params,
      };
    }

    case "report-builder": {
      // Note: the actual param used throughout the codebase is "report_id", not "id"
      const reportId = searchParams.get("report_id") || searchParams.get("id");
      const isNew = searchParams.get("new");
      const params: Record<string, string> = {};
      if (reportId) params.report_id = reportId;
      if (isNew) params.new = isNew;
      if (searchParams.get("mode") === "pro") params.mode = "pro";
      return {
        appId: "report-builder",
        title: "Report Builder",
        params,
      };
    }

    case "pulse-v2": {
      const packId = searchParams.get("pack");
      const params: Record<string, string> = {};
      if (packId) params.packId = packId;
      return {
        appId: "pulse-v2",
        title: getInsightPackWindowTitle(packId),
        params,
      };
    }

    case "documents": {
      if (resourceId) {
        return {
          appId: "document",
          title: "Document",
          params: { id: resourceId },
        };
      }
      return {
        appId: "documents",
        title: "Documents",
        params: {},
      };
    }

    case "settings": {
      const params: Record<string, string> = {};
      const section = searchParams.get("section");
      if (section) {
        params.section = section;
      }
      return {
        appId: "settings",
        title: "Settings",
        params,
      };
    }

    case "home": {
      const homeParams = homeCodec.toParams(
        homeCodec.fromUrl(pathOnly, searchParams),
      );
      return {
        appId: "home",
        title: "Home",
        params: homeParams,
      };
    }

    case "codex": {
      if (resourceId === "tasks" && thirdSegment) {
        return {
          appId: "codex",
          title: "Task",
          params: { taskKey: decodeURIComponent(thirdSegment) },
        };
      }
      if (resourceId === "agents" && thirdSegment) {
        return {
          appId: "codex",
          title: "Agent",
          params: { agentId: thirdSegment },
        };
      }
      if (resourceId === "missions" && thirdSegment) {
        const fourthSegment = pathSegments[3];
        const fifthSegment = pathSegments[4];
        if (fourthSegment === "tasks" && fifthSegment) {
          return {
            appId: "codex",
            title: "Task",
            params: { missionId: thirdSegment, taskKey: decodeURIComponent(fifthSegment) },
          };
        }
        const params: Record<string, string> = { missionId: thirdSegment };
        const view = searchParams.get("view");
        if (view === "network" || view === "brief" || view === "blueprint" || view === "requirements") params.view = view;
        return {
          appId: "codex",
          title: "Mission Blueprint",
          params,
        };
      }
      if (resourceId === "company-brain") {
        return {
          appId: "company-brain",
          title: "Company Brain",
          params: {},
        };
      }
      const codexParams: Record<string, string> = {};
      const library = searchParams.get("library") ?? searchParams.get("tab");
      if (library?.trim()) {
        codexParams.library = library.trim();
      }
      return {
        appId: "codex",
        title: "Codex",
        params: codexParams,
      };
    }

    case "case-checking": {
      const loc = parseCaseCheckingFromRoute(resourceId, searchParams);
      if (loc.view === "rules") {
        return {
          appId: "case-checking",
          title: "Case Checking Rules",
          params: serializeCaseCheckingToOsParams(loc),
        };
      }
      if (loc.caseId) {
        return {
          appId: "case-checking",
          title: "Case Checking",
          params: serializeCaseCheckingToOsParams(loc),
        };
      }
      return {
        appId: "case-checking",
        title: "Case Checking",
        params: serializeCaseCheckingToOsParams(loc),
      };
    }

    case "disclosures": {
      const loc = parseDisclosureFromRoute(resourceId, searchParams);
      return {
        appId: "disclosures",
        title: loc.caseId ? "Disclosure" : "Disclosures",
        params: serializeDisclosureToOsParams(loc),
      };
    }

    case "what-if": {
      // /what-if/portfolio/:portfolioId or /what-if/portfolio/:portfolioId/scenario/:scenarioId
      if (resourceId === "portfolio" && thirdSegment) {
        const fourthSegment = pathSegments[3];
        const fifthSegment = pathSegments[4];
        const params: Record<string, string> = { portfolioId: thirdSegment };
        // /what-if/portfolio/:portfolioId/scenario/:scenarioId
        if (fourthSegment === "scenario" && fifthSegment) {
          params.scenarioId = fifthSegment;
        }
        return {
          appId: "what-if",
          title: "What If Analysis",
          params,
        };
      }
      // /what-if -> base what-if page
      return {
        appId: "what-if",
        title: "What If",
        params: {},
      };
    }

    case "scenario-tracker": {
      if (resourceId === "detail") {
        const params: Record<string, string> = {};
        [
          "instanceId",
          "analysisId",
          "category",
          "metric",
          "scenario",
          "scenarioCommentary",
        ].forEach((key) => {
          const value = searchParams.get(key);
          if (value) params[key] = value;
        });

        return {
          appId: "scenario-metric-detail",
          title: `${params.category || "Scenario"} - ${
            params.metric ? formatScenarioMetric(params.metric) : "Metric"
          }${
            params.scenario ? ` (${params.scenario})` : ""
          }`,
          params,
        };
      }

      if (resourceId === "article") {
        const params: Record<string, string> = {};
        [
          "instanceId",
          "articleId",
          "headline",
          "source",
          "publishedDate",
          "url",
        ].forEach((key) => {
          const value = searchParams.get(key);
          if (value) params[key] = value;
        });

        return {
          appId: "scenario-article-detail",
          title: params.headline || "Scenario Article",
          params,
        };
      }

      if (resourceId) {
        return {
          appId: "scenario-tracker",
          title: "Scenario Tracker",
          params: { id: resourceId },
        };
      }

      const tab = searchParams.get("tab") || "exec-summary";
      const appId = SCENARIO_TRACKER_APP_BY_TAB[tab] || "scenario-tracker";
      const tabApp = SCENARIO_TRACKER_TAB_APPS[appId];

      return {
        appId,
        title: tabApp?.title || "Scenario Tracker",
        params: {},
      };
    }

    case "add-products": {
      const params: Record<string, string> = {};
      const mode = searchParams.get("mode");
      const assetClass = searchParams.get("assetClass");
      if (mode) params.mode = mode;
      if (assetClass) params.assetClass = assetClass;
      return {
        appId: "add-products",
        title: "Add Products",
        params,
      };
    }

    case "calculators": {
      const fromQuery = parseCalculatorsFromSearchParams(searchParams);
      const calculatorId = resourceId || fromQuery.calculatorId;
      const osParams = serializeCalculatorsToOsParams({
        calculatorId,
        faClientId: fromQuery.faClientId,
      });
      if (calculatorId) {
        osParams[NAV_KEYS.calculatorId] = calculatorId;
      }
      return {
        appId: "calculators",
        title: calculatorsWindowTitle({ calculatorId, faClientId: fromQuery.faClientId }),
        params: osParams,
      };
    }

    default: {
      // Standard route-to-appId mapping
      const appId = ROUTE_TO_APP_ID[basePath];
      if (appId) {
        return {
          appId,
          title: formatAppTitle(appId),
          params: resourceId ? { id: resourceId } : {},
        };
      }
    }
  }

  return null;
}

/**
 * Format an appId into a human-readable title.
 */
function formatAppTitle(appId: string): string {
  return appId
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Get the route-to-appId mapping for external use.
 * Useful for components that need the mapping directly.
 */
export function getRouteToAppIdMap(): Record<string, string> {
  return { ...ROUTE_TO_APP_ID };
}
